import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Network
import { request } from "app/network";

interface ApprovePointRequestModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    requestData: {
        seqId: number;
        userName: string;
        requestPoint: string;
        requestMessage: string;
        crtDt: string;
        userPhone?: string;
        senderPhone?: string;
        receiverPhone?: string;
        giftishowProduct?: {
            goodsCode: string;
            goodsName: string;
            brandName: string;
            goodsImgS: string;
            goodsImgB?: string;
        };
    } | null;
}

const ApprovePointRequestModal: React.FC<ApprovePointRequestModalProps> = ({
    open,
    onClose,
    onSuccess,
    requestData,
}) => {
    const [adminMemo, setAdminMemo] = useState("");
    const [loading, setLoading] = useState(false);

    const handleApprove = async () => {
        if (!requestData) return;

        try {
            setLoading(true);

            await request("PointUseRequest.approve", {
                paramz: {
                    seqId: requestData.seqId,
                    adminMemo: adminMemo || null,
                },
                noRetry: true, // 금액 관련 API이므로 자동 재시도 방지
            });

            setLoading(false);
            setAdminMemo("");
            alert("승인 되었습니다.");
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("포인트 사용 요청 승인 실패:", error);
            setLoading(false);
            alert(error?.response?.data?.message || "승인 처리 중 오류가 발생했습니다.");
        }
    };

    const handleClose = () => {
        if (!loading) {
            setAdminMemo("");
            onClose();
        }
    };

    if (!requestData) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle>
                <MDTypography variant="h5" fontWeight="bold">
                    포인트 사용 요청 승인
                </MDTypography>
            </DialogTitle>
            <DialogContent>
                <MDBox pt={1} pb={2}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                            <MDTypography variant="body2" color="text">
                                사용자
                            </MDTypography>
                        </Grid>
                        <Grid item xs={9}>
                            <MDTypography variant="body2" fontWeight="bold">
                                {requestData.userName}
                            </MDTypography>
                        </Grid>

                        <Grid item xs={3}>
                            <MDTypography variant="body2" color="text">
                                요청 포인트
                            </MDTypography>
                        </Grid>
                        <Grid item xs={9}>
                            <MDTypography variant="h6" fontWeight="bold" color="warning">
                                {requestData.requestPoint}
                            </MDTypography>
                        </Grid>


                        {requestData.giftishowProduct && (
                            <>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <MDTypography variant="subtitle2" fontWeight="bold" color="dark">
                                        기프티쇼 상품 정보
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={12} display="flex" alignItems="center" gap={2}>
                                    <MDBox
                                        component="img"
                                        src={requestData.giftishowProduct.goodsImgB || requestData.giftishowProduct.goodsImgS}
                                        alt={requestData.giftishowProduct.goodsName}
                                        width="150px"
                                        height="150px"
                                        borderRadius="lg"
                                        sx={{ objectFit: "contain", border: "1px solid #eee", backgroundColor: "#fff" }}
                                    />
                                    <MDBox>
                                        <MDTypography variant="caption" color="text">
                                            {requestData.giftishowProduct.brandName}
                                        </MDTypography>
                                        <MDTypography variant="body2" fontWeight="bold">
                                            {requestData.giftishowProduct.goodsName}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                        </Grid>

                        <Grid item xs={3}>
                            <MDTypography variant="body2" color="text">
                                사용 쿠폰
                            </MDTypography>
                        </Grid>
                        <Grid item xs={9}>
                            <MDTypography variant="body2" fontWeight="bold">
                                {requestData.requestMessage || "-"}
                            </MDTypography>
                        </Grid>

                        {requestData.userPhone && (
                            <>
                                <Grid item xs={3}>
                                    <MDTypography variant="body2" color="text">
                                        사용자 연락처
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={9}>
                                    <MDTypography variant="body2" fontWeight="bold">
                                        {requestData.userPhone}
                                    </MDTypography>
                                </Grid>
                            </>
                        )}

                        {requestData.receiverPhone && (
                            <>
                                <Grid item xs={3}>
                                    <MDTypography variant="body2" color="text">
                                        수신자 연락처
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={9}>
                                    <MDTypography variant="body2" fontWeight="bold">
                                        {requestData.receiverPhone}
                                    </MDTypography>
                                </Grid>
                            </>
                        )}

                        {requestData.senderPhone && (
                            <>
                                <Grid item xs={3}>
                                    <MDTypography variant="body2" color="text">
                                        발신번호
                                    </MDTypography>
                                </Grid>
                                <Grid item xs={9}>
                                    <MDTypography variant="body2" fontWeight="bold">
                                        {requestData.senderPhone}
                                    </MDTypography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={3}>
                            <MDTypography variant="body2" color="text">
                                요청일시
                            </MDTypography>
                        </Grid>
                        <Grid item xs={9}>
                            <MDTypography variant="body2" color="text">
                                {requestData.crtDt}
                            </MDTypography>
                        </Grid>
                    </Grid>

                    <MDBox mt={3}>
                        <MDTypography variant="body2" color="text" mb={1}>
                            관리자 메모 (선택사항)
                        </MDTypography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="승인 메모를 입력하세요"
                            value={adminMemo}
                            onChange={(e) => setAdminMemo(e.target.value)}
                            disabled={loading}
                            variant="outlined"
                        />
                    </MDBox>
                </MDBox>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleClose}
                    disabled={loading}
                >
                    취소
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleApprove}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : "승인"}
                </MDButton>
            </DialogActions>
        </Dialog >
    );
};

export default ApprovePointRequestModal;
